import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { booleanIntersects } from '@turf/boolean-intersects'

import { downloadBlob } from '@library/FileDownload/file-download.utils'

import './features-download-control.styl'

const TARGET_SOURCES = [
  'environment',
  'environment-osm',
  'breeding-sites',
  'traps',
  'planner-traps',
  'mosquito-observations',
  'alerts',
]

const TARGET_LAYER_IDS = [
  'environment-polygon-layer',
  'environment-line-layer',
  'environment-layer',
  'environment-layer-osm',
  'breeding-sites-polygon-layer',
  'breeding-sites-line-layer',
  'breeding-sites-layer',
  'breeding-sites-layer-osm',
  'trap-layer',
  'planner-traps-layer',
  'mosquito-observations-layer',
  'alerts-layer-bg',
  'alerts-layer',
]

const drawStyles = [
  {
    id: 'feature-download-draw-polygon-fill',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': '#3bb2d0',
      'fill-opacity': 0.1,
    },
  },
  {
    id: 'feature-download-draw-lines',
    type: 'line',
    filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': '#3bb2d0',
      'line-dasharray': [0.2, 2],
      'line-width': 2,
    },
  },
  {
    id: 'feature-download-draw-vertex',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 5,
      'circle-color': '#3bb2d0',
    },
  },
]

const parseJsonString = (value) => {
  if (typeof value !== 'string') return value

  const trimmedValue = value.trim()
  const couldBeJson = (
    (trimmedValue.startsWith('{') && trimmedValue.endsWith('}'))
    || (trimmedValue.startsWith('[') && trimmedValue.endsWith(']'))
  )

  if (!couldBeJson) return value

  try {
    return JSON.parse(trimmedValue)
  } catch {
    return value
  }
}

const parseDownloadValue = (value) => {
  const parsedValue = parseJsonString(value)

  if (Array.isArray(parsedValue)) {
    return parsedValue.map(item => parseDownloadValue(item))
  }

  if (parsedValue !== null && typeof parsedValue === 'object') {
    return Object.fromEntries(
      Object.entries(parsedValue).map(([key, nestedValue]) => [key, parseDownloadValue(nestedValue)]),
    )
  }

  return parsedValue
}

const parseDownloadProperties = (properties = {}) => {
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => [key, parseDownloadValue(value)]),
  )
}

const getOsmProperties = (properties = {}) => {
  const parsedOsm = parseDownloadValue(properties.osm)
  const osm = parsedOsm && typeof parsedOsm === 'object' && !Array.isArray(parsedOsm) ? parsedOsm : {}

  return {
    ...osm,
    ...(properties.providerId !== undefined ? { providerId: properties.providerId } : {}),
    ...(properties.type !== undefined ? { type: properties.type } : {}),
    ...(properties.tags !== undefined ? { tags: parseDownloadValue(properties.tags) } : {}),
    ...(properties.nodes !== undefined ? { nodes: parseDownloadValue(properties.nodes) } : {}),
    ...(properties.members !== undefined ? { members: parseDownloadValue(properties.members) } : {}),
  }
}

const normalizeDownloadProperties = (feature) => {
  const properties = parseDownloadProperties(feature.properties || {})

  if (properties.source !== 'osm') {
    return {
      ...properties,
      mapSource: feature.source,
    }
  }

  const osm = getOsmProperties(properties)
  delete properties.providerId
  delete properties.type
  delete properties.tags
  delete properties.nodes
  delete properties.members
  delete properties.osm

  return {
    ...properties,
    osm,
    mapSource: feature.source,
  }
}

const getFeatureKey = (feature) => {
  const osm = getOsmProperties(feature.properties)
  const providerId = osm.providerId
  const propertyId = providerId ?? feature.properties?.id ?? feature.properties?._id ?? feature.properties?.customId ?? feature.properties?.cluster_id
  const id = feature.id ?? propertyId

  if (id !== undefined && id !== null) {
    return [
      feature.source,
      feature.properties?.source,
      feature.properties?.mission,
      feature.properties?.dataType,
      osm.type,
      id,
    ].join(':')
  }

  return `${feature.source}:${JSON.stringify(feature.geometry)}:${JSON.stringify(feature.properties || {})}`
}

const getSelectionQueryGeometry = (map, selectionFeature) => {
  const coordinates = selectionFeature.geometry.coordinates.flat()
  const projected = coordinates.map(coordinate => map.project(coordinate))
  const xValues = projected.map(point => point.x)
  const yValues = projected.map(point => point.y)

  return [
    [Math.min(...xValues), Math.min(...yValues)],
    [Math.max(...xValues), Math.max(...yValues)],
  ]
}

const isTargetFeature = (feature, selectionFeature) => {
  if (!TARGET_SOURCES.includes(feature.source)) return false

  try {
    return booleanIntersects(feature, selectionFeature)
  } catch {
    return false
  }
}

const toDownloadFeature = feature => ({
  type: 'Feature',
  ...(feature.id !== undefined && feature.id !== null ? { id: feature.id } : {}),
  geometry: feature.geometry,
  properties: normalizeDownloadProperties(feature),
})

class FeaturesDownloadControl {
  constructor(map) {
    this._container = null
    this.button = null
    this.draw = null
    this.notice = null
    this.isActive = false
    this.map = map

    this.handleDrawCreate = this.handleDrawCreate.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
  }

  onAdd() {
    this._container = document.createElement('div')
    this._container.className = 'maplibregl-ctrl maplibregl-ctrl-group maplibregl-ctrl-features-download'

    const button = document.createElement('button')
    this.button = button
    button.type = 'button'
    button.title = 'Download features in polygon'
    button.onclick = () => this.toggleSelection()

    this._container.appendChild(button)

    return this._container
  }

  onRemove() {
    this.stopSelection()
    this.hideNotice()

    if (this._container?.parentNode) {
      this._container.parentNode.removeChild(this._container)
    }
  }

  showNotice(message, onCancel = () => this.hideNotice()) {
    this.hideNotice()

    const notice = document.createElement('div')
    notice.className = 'map-feature-download-notice'
    notice.role = 'dialog'
    notice.ariaLive = 'polite'

    const content = document.createElement('div')
    content.className = 'map-feature-download-notice__content'

    const text = document.createElement('p')
    text.textContent = message

    const button = document.createElement('button')
    button.type = 'button'
    button.textContent = 'Cancel'
    button.onclick = onCancel

    content.appendChild(text)
    content.appendChild(button)
    notice.appendChild(content)
    this.map.getContainer().appendChild(notice)
    this.notice = notice
  }

  hideNotice() {
    if (this.notice?.parentNode) {
      this.notice.parentNode.removeChild(this.notice)
    }

    this.notice = null
  }

  toggleSelection() {
    if (this.isActive) {
      this.stopSelection()
      return
    }

    this.startSelection()
  }

  startSelection() {
    if (this.hasActiveDrawControl()) {
      this.showNotice('Finish the current drawing before downloading features.')
      return
    }

    this.showNotice('Draw a polygon on the map to download rendered features inside it.', () => this.stopSelection())

    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      styles: drawStyles,
    })

    this.map.addControl(this.draw)
    this.map.on('draw.create', this.handleDrawCreate)
    document.addEventListener('keydown', this.handleKeyDown)

    this.draw.changeMode('draw_polygon')
    this.map.getCanvas().classList.add('crosshair-cursor')
    this._container.classList.add('maplibregl-ctrl-features-download--active')
    this.button.title = 'Cancel feature download selection'
    this.isActive = true
  }

  stopSelection() {
    this.hideNotice()

    if (!this.isActive && !this.draw) return

    this.map.off('draw.create', this.handleDrawCreate)
    document.removeEventListener('keydown', this.handleKeyDown)
    this.map.getCanvas().classList.remove('crosshair-cursor')
    this._container?.classList.remove('maplibregl-ctrl-features-download--active')

    if (this.button) {
      this.button.title = 'Download features in polygon'
    }

    if (this.draw && this.map._controls?.includes(this.draw)) {
      this.draw.deleteAll()
      this.map.removeControl(this.draw)
    }

    this.draw = null
    this.isActive = false
  }

  hasActiveDrawControl() {
    return this.map._controls?.some(control => control instanceof MapboxDraw)
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.stopSelection()
    }
  }

  handleDrawCreate(event) {
    const selectionFeature = event.features?.[0]

    if (selectionFeature?.geometry?.type === 'Polygon') {
      this.downloadFeatures(selectionFeature)
    }

    window.setTimeout(() => this.stopSelection())
  }

  downloadFeatures(selectionFeature) {
    const layers = TARGET_LAYER_IDS.filter(layerId => this.map.getLayer(layerId))
    const queryGeometry = getSelectionQueryGeometry(this.map, selectionFeature)
    const renderedFeatures = this.map.queryRenderedFeatures(queryGeometry, { layers })
    const uniqueFeatures = new Map()

    renderedFeatures
      .filter(feature => isTargetFeature(feature, selectionFeature))
      .forEach((feature) => {
        uniqueFeatures.set(getFeatureKey(feature), toDownloadFeature(feature))
      })

    const featureCollection = {
      type: 'FeatureCollection',
      features: Array.from(uniqueFeatures.values()),
    }

    const blob = new Blob([JSON.stringify(featureCollection, null, 2)], { type: 'application/geo+json' })
    downloadBlob(blob, `map-features-${new Date().toISOString().replace(/[:.]/g, '-')}.geojson`)
  }
}

export default FeaturesDownloadControl
